import { Injectable } from '@angular/core';
import { IEmailMessageHistory, IMessageAttachment } from '@dxp/core/models';
import { Observable, from, forkJoin, map, switchMap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class EmailTranscriptService {
    createTranscript(
        emailMessageList: IEmailMessageHistory[],
    ): Observable<Blob> {
        const lastEmail = [...emailMessageList].sort((a, b) =>
            a.creationDate.localeCompare(b.creationDate),
        )[emailMessageList.length - 1];

        const attachments = emailMessageList
            .filter(m => m.attachments?.length)
            .flatMap(m => m.attachments ?? []);

        return this.#generateMessageWithAttachments(
            lastEmail,
            attachments,
        ).pipe(
            map(transcript => {
                const content = `data:message/rfc822 eml,\nContent-Type: text/plain;charset="utf-8"\n${transcript}`;
                return new Blob([content], {
                    type: 'text/plain;charset=utf-8',
                });
            }),
        );
    }

    #generateMessageWithAttachments(
        email: IEmailMessageHistory,
        attachments: IMessageAttachment[],
    ): Observable<string> {
        return this.#processAttachments(attachments).pipe(
            map(encodedAttachments => {
                const headers: string[] = [];

                const formatAddr = (name: string, addr: string) =>
                    name ? `"${name}" <${addr}>` : addr;

                headers.push(
                    `Date: ${new Date(email.creationDate).toUTCString().replace(/GMT|UTC/gi, '+0000')}`,
                );
                headers.push(
                    `From: ${formatAddr(email.from.name ?? '', email.from.address)}`,
                );
                headers.push(
                    `To: ${email.toRecipients.map(r => r.address).join(', ')}`,
                );
                if (email.ccRecipients?.length) {
                    headers.push(
                        `Cc: ${email.ccRecipients.map(r => r.address).join(', ')}`,
                    );
                }
                if (email.bccRecipients?.length) {
                    headers.push(
                        `Bcc: ${email.bccRecipients.map(r => r.address).join(', ')}`,
                    );
                }
                headers.push(`Subject: ${email.subject ?? ''}`);
                const boundary = '----=_Part_0_123456789.1234567890123';

                headers.push(`MIME-Version: 1.0`);
                headers.push(
                    `Content-Type: multipart/mixed; boundary="${boundary}"`,
                );

                const bodyParts: string[] = [];

                const bodyContentType =
                    email.body?.contentType === 'Html'
                        ? 'text/html'
                        : 'text/plain';
                const bodyData = email.body?.content ?? '';

                bodyParts.push(`--${boundary}`);
                bodyParts.push(
                    `Content-Type: ${bodyContentType}; charset="utf-8"`,
                );
                bodyParts.push(`Content-Transfer-Encoding: 7bit\n`);
                bodyParts.push(bodyData);

                for (const att of encodedAttachments) {
                    bodyParts.push(`--${boundary}`);
                    bodyParts.push(
                        `Content-Type: ${att.contentType}; name="${att.name}"`,
                    );
                    bodyParts.push('Content-Transfer-Encoding: base64');
                    bodyParts.push(
                        `Content-Disposition: attachment; filename="${att.name}"\n`,
                    );
                    bodyParts.push(att.base64);
                }

                bodyParts.push(`--${boundary}--`);

                return [...headers, '', ...bodyParts].join('\n');
            }),
        );
    }

    #processAttachments(
        attachments: IMessageAttachment[],
    ): Observable<{ name: string; contentType: string; base64: string }[]> {
        if (attachments.length === 0) {
            return from([[]]);
        }

        const observables = attachments.map(att =>
            from(fetch(att.contentUrl)).pipe(
                switchMap(response => from(response.blob())),
                switchMap(blob => this.#blobToBase64(blob)),
                map(base64 => ({
                    name: att.name,
                    contentType: att.contentType,
                    base64,
                })),
            ),
        );

        return forkJoin(observables);
    }

    #blobToBase64(blob: Blob): Observable<string> {
        return new Observable<string>(observer => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                observer.next(result.split(',')[1]);
                observer.complete();
            };
            reader.onerror = err => observer.error(err);
            reader.readAsDataURL(blob);
        });
    }
}
